// src/components/hooks/useDeviceData.ts
import { useCallback } from "react";
import { useOrgSettings } from "@/hooks/useOrgSettings";
import type { Lead } from "@/types/lead";

export type UseDeviceDataArgs<TSalesperson, TLocation> = {
  storageKey: string;
  version: string;

  // current data
  salesperson: TSalesperson | null;
  location: TLocation | null;
  leads: Lead[];

  // state setters / refs
  setSalesperson: (v: TSalesperson | null) => void;
  setLocation: (v: TLocation | null) => void;
  setLeads: React.Dispatch<React.SetStateAction<Lead[]>>;
  setCurrentLead: (v: Lead | null) => void;

  currentLeadRef: React.MutableRefObject<Lead | null>;
  transcriptRef: React.MutableRefObject<string>;
  listeningRef: React.MutableRefObject<boolean>;

  // UI setters
  setTranscript: (v: string) => void;
  setIsListening: (v: boolean) => void;
  setMergeSuggestion: (v: any) => void; // keep loose; your MergeSuggestion is local to LeadWorkspace
};

export type UseDeviceDataResult = {
  resetAllData: () => void;
  resetSetupOnly: () => void;
  copyLeadsJson: () => Promise<void>;
};

export function useDeviceData<TSalesperson, TLocation>({
  storageKey,
  version,

  salesperson,
  location,
  leads,

  setSalesperson,
  setLocation,
  setLeads,
  setCurrentLead,

  currentLeadRef,
  transcriptRef,
  listeningRef,

  setTranscript,
  setIsListening,
  setMergeSuggestion,
}: UseDeviceDataArgs<TSalesperson, TLocation>): UseDeviceDataResult {
  const { appDisplayName } = useOrgSettings();

  const resetAllData = useCallback(() => {
    if (
      !window.confirm(
        `Reset ${appDisplayName} on this device? This will clear all saved leads, profile, and lot info.`
      )
    ) {
      return;
    }

    try {
      localStorage.removeItem(storageKey);
    } catch (err) {
      console.warn("Failed to remove localStorage key:", err);
    }

    setSalesperson(null);
    setLocation(null);
    setLeads([]);
    setCurrentLead(null);
    currentLeadRef.current = null;

    setTranscript("");
    transcriptRef.current = "";

    setIsListening(false);
    listeningRef.current = false;

    setMergeSuggestion(null);
  }, [
    appDisplayName,
    storageKey,
    setSalesperson,
    setLocation,
    setLeads,
    setCurrentLead,
    currentLeadRef,
    setTranscript,
    transcriptRef,
    setIsListening,
    listeningRef,
    setMergeSuggestion,
  ]);

  const copyLeadsJson = useCallback(async () => {
    const payload = {
      version,
      salesperson,
      location,
      leads,
    };

    const text = JSON.stringify(payload, null, 2);

    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.warn("Clipboard write failed, logging JSON instead.", err);
      console.log(`${appDisplayName} export:`, text);
    }
  }, [appDisplayName, version, salesperson, location, leads]);

  const resetSetupOnly = useCallback(() => {
    if (!window.confirm("Clear setup info (name & location) on this device and rerun setup?")) {
      return;
    }

    // Clear only setup-related state
    setSalesperson(null);
    setLocation(null);

    // Clear transcript + listening
    setTranscript("");
    transcriptRef.current = "";
    setIsListening(false);
    listeningRef.current = false;

    // Persist the cleared setup but KEEP leads
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        localStorage.setItem(
          storageKey,
          JSON.stringify({
            ...parsed,
            salesperson: null,
            location: null,
          })
        );
      }
    } catch (err) {
      console.warn("Failed to reset setup-only storage:", err);
    }
  }, [
    storageKey,
    setSalesperson,
    setLocation,
    setTranscript,
    transcriptRef,
    setIsListening,
    listeningRef,
  ]);

  return { resetAllData, resetSetupOnly, copyLeadsJson };
}